import { supabase } from '@/integrations/supabase/client';
import { PracticeDataNormalizer, NormalizedPracticeData } from '@/utils/practiceDataNormalizer';
import { logger } from '@/utils/logger';

export class GeneratedContentService {
  // PHASE 6: Track in-flight requests to prevent duplicates
  private static audioRequests = new Map<string, Promise<any>>();

  static async getModuleQuiz(moduleId: string, courseId: string) {
    const { data } = await supabase
      .from('module_quizzes')
      .select('questions')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .maybeSingle();

    return data?.questions || null;
  }

  static async getPracticeLab(moduleId: string, courseId: string): Promise<NormalizedPracticeData | null> {
    const { data, error } = await supabase
      .from('generated_practice_labs')
      .select('practice_data, estimated_time, difficulty_level')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .maybeSingle();

    if (error) {
      logger.error('Error fetching practice lab:', error);
      return null;
    }

    if (!data?.practice_data) {
      logger.debug('No practice data found for module:', moduleId);
      return null;
    }

    // Normalize the practice data to ensure consistent structure
    const normalized = PracticeDataNormalizer.normalize(data.practice_data);
    
    if (!normalized) {
      logger.error('Failed to normalize practice data for module:', { moduleId, practiceData: data.practice_data });
      return null;
    }

    // Merge in metadata from the lab record
    return {
      ...normalized,
      estimatedTime: normalized.estimatedTime || data.estimated_time,
      difficulty: normalized.difficulty || data.difficulty_level
    };
  }

  static async getFinalAssessment(courseId: string) {
    const { data } = await supabase
      .from('generated_final_assessments')
      .select('assessment_data, total_questions, time_limit_minutes')
      .eq('course_id', courseId)
      .maybeSingle();

    return data?.assessment_data || null;
  }

  static async getModuleAudio(moduleId: string, courseId: string, retryCount = 0) {
    const key = `${courseId}:${moduleId}`;

    console.log(`🎧 [GeneratedContent] Getting audio for ${moduleId}:`, { retryCount });

    // PHASE 4: Instant cache return - NO timeout for cached data
    const { audioMetadataCache } = await import('./audio/AudioMetadataCache');
    const cached = audioMetadataCache.get(moduleId, courseId);
    
    if (cached !== null) {
      console.log('⚡ Audio metadata from cache (instant):', moduleId);
      return cached.audioUrl ? {
        audio_url: cached.audioUrl,
        duration_seconds: cached.duration,
        module_id: moduleId,
        course_id: courseId
      } : null;
    }

    // PHASE 6: Return in-flight request if exists (cache miss only)
    if (this.audioRequests.has(key)) {
      logger.debug('[GeneratedContent] Reusing in-flight audio request:', key);
      return this.audioRequests.get(key);
    }

    // Cache miss - fetch with timeout (rare case)
    const promise = this.fetchAudioWithTimeout(moduleId, courseId, retryCount);
    this.audioRequests.set(key, promise);

    try {
      const result = await promise;
      return result;
    } finally {
      // Cleanup after 5 seconds
      setTimeout(() => {
        this.audioRequests.delete(key);
      }, 5000);
    }
  }

  // PHASE 2 & 4: Fetch audio with 15s timeout and retry logic (only for cache miss - rare)
  private static async fetchAudioWithTimeout(moduleId: string, courseId: string, retryCount = 0) {
    const timeoutDuration = 15000; // PHASE 2: Increased from 30s to 15s for faster feedback
    const maxRetries = 3;

    console.log(`🔍 [GeneratedContent] Fetching audio from DB (attempt ${retryCount + 1}/${maxRetries}):`, {
      moduleId,
      courseId,
      timeout: timeoutDuration
    });

    const timeoutPromise = new Promise<null>((_, reject) => 
      setTimeout(() => reject(new Error('Audio lookup timeout')), timeoutDuration)
    );

    const fetchPromise = supabase
      .from('module_audio')
      .select('audio_url, duration_seconds')
      .eq('module_id', moduleId)
      .eq('course_id', courseId)
      .maybeSingle();

    try {
      const { data, error } = await Promise.race([fetchPromise, timeoutPromise]) as any;

      if (error) {
        console.error('❌ [GeneratedContent] Database error:', error);
        throw error;
      }

      console.log(`✅ [GeneratedContent] Audio fetch result:`, {
        moduleId,
        hasAudio: !!data?.audio_url,
        audioUrl: data?.audio_url,
        duration: data?.duration_seconds
      });

      // Only cache successful fetches or definitive "no audio" results
      const { audioMetadataCache } = await import('./audio/AudioMetadataCache');
      if (data?.audio_url) {
        audioMetadataCache.set(moduleId, courseId, data.audio_url, data.duration_seconds);
        console.log(`💾 [GeneratedContent] Cached audio URL for ${moduleId}`);
      } else {
        // Definitive "no audio in DB" - safe to cache to prevent repeated queries
        audioMetadataCache.set(moduleId, courseId, null, null);
        console.log(`💾 [GeneratedContent] Cached "no audio" result for ${moduleId}`);
      }

      return data || null;
    } catch (error: any) {
      const isTimeout = error.message === 'Audio lookup timeout';
      
      console.error(`❌ [GeneratedContent] Fetch failed (${isTimeout ? 'timeout' : 'error'}):`, {
        moduleId,
        retryCount,
        error: error.message
      });

      // PHASE 4: Retry logic with exponential backoff
      if (retryCount < maxRetries) {
        const backoffDelay = Math.min(2000 * Math.pow(2, retryCount), 10000); // 2s, 4s, 8s max
        console.warn(`⏳ [GeneratedContent] Retrying in ${backoffDelay}ms... (attempt ${retryCount + 2}/${maxRetries})`);
        
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return this.fetchAudioWithTimeout(moduleId, courseId, retryCount + 1);
      }
      
      console.error(`💀 [GeneratedContent] All ${maxRetries} retry attempts failed for ${moduleId}`);
      // Don't cache failures - allow user to manually retry
      return null;
    }
  }

  static async getGenerationStatus() {
    const { data } = await supabase
      .from('system_generation_status')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000001')
      .single();

    return data;
  }

  static subscribeToGenerationStatus(callback: (status: any) => void) {
    return supabase
      .channel('generation-status')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'system_generation_status'
        },
        (payload) => callback(payload.new)
      )
      .subscribe();
  }
}
